/**
 * Collection "Load more" — appends/prepends pages via Shopify's Section Rendering API,
 * updates the URL with history.pushState, and keeps the numbered pagination + the
 * prev/next buttons in sync with the page you're on.
 *
 * Button visibility follows the CURRENT page (the ?page=N in the URL, which is also the
 * highlighted number in the pagination strip), not what happens to be stacked on screen:
 *   - "Load previous" shows whenever the current page > 1        (target = current - 1)
 *   - "Load more"     shows whenever the current page < last page (target = current + 1)
 * So it's identical however you arrived — numbered link, Load more/previous, or a direct URL.
 *
 * Clicking a button fetches the target page (for the authoritative pagination strip), then
 * only inserts that page's cards if they aren't already in the grid — so nothing duplicates.
 * If the target is already on screen it just moves the URL and scrolls to it.
 *
 * The server still renders the buttons for the no-JS / crawlable fallback; once this element
 * upgrades it becomes authoritative.
 */
class LoadMoreProducts extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#syncButtons();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  get #totalPages() {
    return Number(this.dataset.totalPages) || 1;
  }

  get #currentPage() {
    return Number(new URL(window.location.href).searchParams.get('page')) || 1;
  }

  #onClick = (event) => {
    const button = event.target.closest('.load-more-products__button');
    if (button) {
      event.preventDefault();
      this.#loadPage(button);
    }
  };

  #grid() {
    return (this.closest('#ResultsList') || document).querySelector('[ref="grid"]');
  }

  /**
   * The set of page numbers currently rendered in the grid, read from each card's data-page.
   * @returns {Set<number>}
   */
  #loadedPages() {
    const grid = this.#grid();
    const pages = new Set();
    if (!grid) return pages;

    for (const card of grid.querySelectorAll('[ref="cards[]"]')) {
      const page = Number(card.dataset.page);
      if (Number.isFinite(page) && page > 0) pages.add(page);
    }
    return pages;
  }

  async #loadPage(button) {
    if (button.getAttribute('aria-busy') === 'true') return;

    const url = new URL(button.href);
    const target = Number(url.searchParams.get('page'));
    const grid = this.#grid();

    if (!target || !grid) {
      window.location.assign(url.toString());
      return;
    }

    const direction = button.dataset.direction;
    const label = button.textContent;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Loading…';

    try {
      const alreadyLoaded = this.#loadedPages().has(target);
      const { cards, doc } = await this.#fetchPage(url);

      if (!alreadyLoaded && cards.length > 0) {
        if (direction === 'previous') {
          this.#prepend(grid, cards);
        } else {
          grid.append(...cards);
        }
      }

      this.#syncPagination(doc);
      history.pushState(null, '', url.toString());

      if (alreadyLoaded) this.#scrollToPage(grid, target);
    } catch (error) {
      console.error('[collection-load-more]', error);
      window.location.assign(url.toString());
      return;
    } finally {
      if (button.isConnected) {
        button.removeAttribute('aria-busy');
        button.textContent = label;
      }
    }

    this.#syncButtons();
  }

  async #fetchPage(url) {
    const request = new URL(url);
    request.searchParams.set('section_id', this.dataset.sectionId);

    const response = await fetch(request.toString());
    if (!response.ok) throw new Error(`Section render failed: ${response.status}`);

    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const grid = doc.querySelector('[ref="grid"]');
    const cards = grid ? Array.from(grid.querySelectorAll(':scope > [ref="cards[]"]')) : [];
    return { cards, doc };
  }

  /**
   * Swaps the rendered pagination controls for the fetched page's version, so the
   * highlighted number always matches the current page.
   * @param {Document} doc - Parsed section HTML for the target page
   */
  #syncPagination(doc) {
    const current = this.querySelector('.pagination');
    const next = doc.querySelector('.pagination');
    if (current && next) current.replaceWith(next);
  }

  #prepend(grid, cards) {
    const anchor = grid.firstElementChild;
    const before = anchor ? anchor.getBoundingClientRect().top : 0;
    grid.prepend(...cards);
    if (anchor) {
      window.scrollBy({ top: anchor.getBoundingClientRect().top - before, behavior: 'instant' });
    }
  }

  /**
   * Scrolls to the first card of an already-loaded page.
   * @param {Element} grid
   * @param {number} page
   */
  #scrollToPage(grid, page) {
    const card = grid.querySelector(`[ref="cards[]"][data-page="${page}"]`);
    card?.scrollIntoView({ behavior: 'instant', block: 'start' });
  }

  /**
   * Reconciles both buttons against the current page. Runs on connect and after every
   * navigation, so visibility is the same however the page was reached.
   */
  #syncButtons() {
    const current = this.#currentPage;
    const total = this.#totalPages;

    this.#reconcileButton('previous', current > 1, current - 1);
    this.#reconcileButton('next', current < total, current + 1);
  }

  /**
   * Shows/hides a directional button and points it at the right page, creating it if the
   * server didn't render it but it's now needed.
   * @param {'previous' | 'next'} direction
   * @param {boolean} shouldShow
   * @param {number} targetPage
   */
  #reconcileButton(direction, shouldShow, targetPage) {
    const actions = this.querySelector('.load-more-products__actions');
    if (!actions) return;

    let button = actions.querySelector(`.load-more-products__button[data-direction="${direction}"]`);

    if (!shouldShow) {
      button?.remove();
      return;
    }

    if (!button) {
      button = document.createElement('a');
      button.className = 'load-more-products__button button';
      button.dataset.direction = direction;
      button.setAttribute('rel', direction === 'previous' ? 'prev' : 'next');
      button.textContent = direction === 'previous' ? 'Load previous' : 'Load more';
      // Previous sits first, next sits last.
      direction === 'previous' ? actions.prepend(button) : actions.append(button);
    }

    const url = new URL(window.location.href);
    url.searchParams.set('page', String(targetPage));
    url.hash = '';
    button.href = url.toString();
  }
}

if (!customElements.get('load-more-products')) {
  customElements.define('load-more-products', LoadMoreProducts);
}
